import { type GetStaticPaths, type GetStaticProps } from 'next';
import { type NextSeoProps } from 'next-seo';

import { ShortUrl } from '@air/api';
import { type ShortUrlForm, ShortUrlObjectType } from '@air/api/types';

import { ContentCollectionWrapper } from '~/components/ContentCollection/ContentCollectionWrapper';
import { ContentCollectionLayout } from '~/components/Layouts/ContentCollectionLayout';
import { getPageUrl } from '~/utils/Url';

type BoardCollectionPageProps = {
  shortId: string;
} & FetchBoardCollectionHeadDataResponse;

export default function BoardCollection({
  seo,
  data,
  shortId,
  requireAccount,
  isOnPaidPlan,
}: BoardCollectionPageProps) {
  return (
    <ContentCollectionLayout data={data} seo={seo} shortId={shortId} isOnPaidPlan={isOnPaidPlan}>
      <ContentCollectionWrapper requireAccount={requireAccount} />
    </ContentCollectionLayout>
  );
}

export type FetchBoardCollectionHeadDataResponse = {
  seo: NextSeoProps;
  data: ShortUrlForm;
  requireAccount: boolean;
  isOnPaidPlan: boolean | undefined;
};

export async function fetchCollectionFormHeadData(shortId: string): Promise<FetchBoardCollectionHeadDataResponse> {
  const getByShortIdResponse = await ShortUrl.getByShortId(shortId);

  if (getByShortIdResponse.type !== ShortUrlObjectType.form) {
    throw new Error('Failed to fetch a form from ShortUrl.getByShortId');
  }

  // TODO: Sort this out. What is the point of this URL
  const url = `https://app.air.inc/d/${shortId}`;
  const title = `${getByShortIdResponse.data.title} / ${getByShortIdResponse.data.workspace.name}`;

  const seo: NextSeoProps = {
    nofollow: true,
    noindex: true,
    title: `${getByShortIdResponse.data.title} / ${getByShortIdResponse.data.workspace.name}`,
    description: getByShortIdResponse.data.description ?? '',
    canonical: url,
    openGraph: {
      url,
      title: `${title} / Air`,
      description: `Submit content to ${getByShortIdResponse.data.workspace.name} quickly and securely on Air`,
    },
  };

  seo.openGraph = {
    ...seo.openGraph,
    images: [{ url: `${getPageUrl()}/assets/air-og-image.png`, width: 1200, height: 630, alt: '' }],
  };

  return {
    seo,
    data: getByShortIdResponse.data,
    requireAccount: getByShortIdResponse.requireAccount,
    isOnPaidPlan: getByShortIdResponse.isOnPaidPlan,
  };
}

export const getStaticProps: GetStaticProps<BoardCollectionPageProps, { shortId?: string }> = async ({ params }) => {
  const shortId = params?.shortId;

  if (!shortId) {
    throw new Error('No shortId provided for board collection');
  }

  try {
    const { data, seo, requireAccount, isOnPaidPlan } = await fetchCollectionFormHeadData(shortId);

    return {
      props: { seo, data, shortId, requireAccount, isOnPaidPlan },
      revalidate: 1,
    };
  } catch (_error) {
    return {
      notFound: true,
      revalidate: 1,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [], // We don't want to statically pre-render path. Only the <head> element for the page on request.
  fallback: 'blocking', // https://nextjs.org/docs/basic-features/data-fetching#fallback-blocking
});
